/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useGame } from '../../../contexts/GameContext';
import { useThemeClassName } from '../../../contexts/ThemeContext';
import StyledButton from '../../buttons/StyledButton';
import useNextDisplayAction from '../useNextDisplayAction';

import styles from '../../../../styles/displays/footers/default-footer.module.scss';

const propTypes = {
    page: PropTypes.shape({}).isRequired,
    game: PropTypes.shape({}).isRequired,
    changeStep: PropTypes.func.isRequired,
    updateStep: PropTypes.func.isRequired,
    className: PropTypes.string,
};

const defaultProps = { className: null };

function DefaultFooter({ page, changeStep, updateStep, className }) {
    const themeClassName = useThemeClassName(styles);
    const game = useGame();

    const {
        slug: pageSlug = null,
        pageGame = null,
        pageStep = null,
        pageSteps: steps = [],
        leaderboard,
    } = page || {};

    const { slug: pageStepSlug = null } = pageStep || {};

    const {
        id: gameId = null,
        step: stepId = null,
        title = null,
        subtitle = null,
        // leaderboard = null,
    } = game || {};

    const stepParts = stepId !== null ? stepId.split('-') : null;
    const questionId = stepParts !== null && stepParts.length > 1 ? stepParts[1] : null;

    const { answers = [], participationsCount = 0 } = leaderboard || {};
    const answer = answers.find(({ id: answerId }) => answerId === questionId) || null;
    const { total: answerParticipations = 0 } = answer || {};

    const step = steps.find(({ id }) => id === stepId) || null;
    const { label = null } = step || {};

    const leaderboardStep = questionId !== null ? `leaderboard-${questionId}` : null;
    const hasLeaderboardStep =
        (steps.find(({ id = null }) => id === leaderboardStep) || null) !== null;
    const hasLeaderboard =
        ((steps || []).find(
            ({ id = null, hideInFooter = true }) => id === leaderboardStep && !hideInFooter,
        ) || null) !== null;

    const onClickLeaderboard = useCallback(() => {
        if (changeStep !== null && hasLeaderboardStep) {
            changeStep(leaderboardStep, gameId);
        }
    }, [changeStep, gameId, leaderboardStep, hasLeaderboardStep]);

    const {
        waiting = false,
        onClickNext = null,
        nextLabel = null,
        locked = false,
    } = useNextDisplayAction({
        game,
        steps,
        pageGame,
        pageStep,
        pageSlug,
        changeStep,
        updateStep,
    });

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [className]: className,
                },
            ])}
        >
            <div className={styles.participations}>
                <p className={styles.line}>
                    <FormattedMessage defaultMessage="Challenge" description="Display label" />{' '}
                    <span className={styles.pill} title={title}>
                        {gameId !== null ? `${subtitle}` : '-'} / {label || stepId || '-'}
                    </span>
                </p>
                <p className={styles.line}>
                    <span>
                        <FormattedMessage defaultMessage="Answers" description="Display label" />
                    </span>
                    <span className={styles.pill}>
                        {answerParticipations} / {participationsCount}{' '}
                    </span>
                    <span>
                        {' '}
                        <FormattedMessage
                            defaultMessage="Participants"
                            description="Display label"
                        />
                    </span>
                </p>
            </div>
            <div className={styles.next}>
                {hasLeaderboard &&
                questionId !== null &&
                pageStepSlug !== leaderboardStep &&
                !waiting ? (
                    <StyledButton
                        onClick={onClickLeaderboard}
                        className={styles.nextButton}
                        disabled={onClickLeaderboard === null || locked}
                    >
                        <FormattedMessage
                            defaultMessage="Leaderboard"
                            description="Display label"
                        />
                    </StyledButton>
                ) : null}
                <StyledButton
                    onClick={onClickNext}
                    className={styles.nextButton}
                    disabled={onClickNext === null || locked}
                >
                    {nextLabel}
                </StyledButton>
            </div>
        </div>
    );
}

DefaultFooter.propTypes = propTypes;
DefaultFooter.defaultProps = defaultProps;

export default DefaultFooter;
